/**
 * Settings → Ad Account → credit-line billing.
 *
 * Apple Ads accounts on a line of credit (paymentModel=LOC) must attach
 * EITHER a Budget Order OR LOC invoice details to every campaign, or
 * Apple parks the campaign ON_HOLD with "Missing budget order". This
 * card lets the admin declare, once per ASA account, whether the
 * account is on a credit line and which billing object Campaign Studio
 * should attach to every campaign it creates.
 *
 * Default OFF (PAYG / card) → nothing is attached, original behaviour.
 */

import { useEffect, useState } from "react";
import { Btn } from "@/components/Btn";
import { Switch } from "@/components/Switch";
import {
  useAsaBilling,
  useAsaBudgetOrders,
  useSaveAsaBilling,
} from "@/lib/api-hooks";
import type { AsaBillingMode } from "@/lib/types";

const EMAIL_RE = /^[^@\s]+@[^@\s]+\.[^@\s]+$/;

function Field({
  label,
  value,
  onChange,
  placeholder,
  required,
}: {
  label: string;
  value: string;
  onChange: (v: string) => void;
  placeholder?: string;
  required?: boolean;
}) {
  return (
    <div
      style={{
        display: "grid",
        gridTemplateColumns: "200px 1fr",
        gap: 12,
        alignItems: "center",
      }}
    >
      <div className="label" style={{ fontSize: 13, color: "var(--c-text-2)" }}>
        {label}
        {required && <span style={{ color: "var(--c-neg-600)" }}> *</span>}
      </div>
      <input
        className="input"
        value={value}
        placeholder={placeholder}
        onChange={(e) => onChange(e.target.value)}
      />
    </div>
  );
}

export function AsaBillingCard() {
  const query = useAsaBilling();
  const save = useSaveAsaBilling();

  const [enabled, setEnabled] = useState(false);
  const [mode, setMode] = useState<AsaBillingMode>("budget_order");
  const [boId, setBoId] = useState<number | null>(null);
  const [boName, setBoName] = useState<string | null>(null);
  const [billingEmail, setBillingEmail] = useState("");
  const [buyerEmail, setBuyerEmail] = useState("");
  const [buyerName, setBuyerName] = useState("");
  const [clientName, setClientName] = useState("");
  const [orderNumber, setOrderNumber] = useState("");
  const [err, setErr] = useState<string | null>(null);
  const [saved, setSaved] = useState(false);

  // Seed local state from the server config once it loads.
  useEffect(() => {
    const d = query.data;
    if (!d) return;
    setEnabled(d.credit_line_enabled);
    setMode(d.billing_mode ?? "budget_order");
    setBoId(d.budget_order_id);
    setBoName(d.budget_order_name);
    setBillingEmail(d.loc_billing_contact_email ?? "");
    setBuyerEmail(d.loc_buyer_email ?? "");
    setBuyerName(d.loc_buyer_name ?? "");
    setClientName(d.loc_client_name ?? "");
    setOrderNumber(d.loc_order_number ?? "");
  }, [query.data]);

  // Live budget-order list — only fetched when the user is actually in
  // budget-order mode (it's a live ASA round-trip).
  const boQuery = useAsaBudgetOrders(enabled && mode === "budget_order");

  const dirtyMark = () => {
    if (saved) setSaved(false);
    if (err) setErr(null);
  };

  const onSave = async () => {
    setErr(null);
    setSaved(false);
    if (enabled) {
      if (mode === "budget_order" && !boId) {
        setErr("Pick a budget order.");
        return;
      }
      if (mode === "loc_invoice") {
        if (!EMAIL_RE.test(billingEmail.trim())) {
          setErr("Billing contact email is invalid.");
          return;
        }
        if (!EMAIL_RE.test(buyerEmail.trim())) {
          setErr("Buyer email is invalid.");
          return;
        }
        if (!buyerName.trim()) {
          setErr("Buyer name is required.");
          return;
        }
      }
    }
    try {
      await save.mutateAsync({
        credit_line_enabled: enabled,
        billing_mode: enabled ? mode : null,
        budget_order_id: enabled && mode === "budget_order" ? boId : null,
        budget_order_name: enabled && mode === "budget_order" ? boName : null,
        loc_billing_contact_email:
          enabled && mode === "loc_invoice" ? billingEmail.trim() : null,
        loc_buyer_email:
          enabled && mode === "loc_invoice" ? buyerEmail.trim() : null,
        loc_buyer_name:
          enabled && mode === "loc_invoice" ? buyerName.trim() : null,
        loc_client_name:
          enabled && mode === "loc_invoice" ? clientName.trim() || null : null,
        loc_order_number:
          enabled && mode === "loc_invoice" ? orderNumber.trim() || null : null,
      });
      setSaved(true);
    } catch (e) {
      const msg =
        (e as { response?: { data?: { error?: { message?: string } } } })
          ?.response?.data?.error?.message ?? "Save failed";
      setErr(msg);
    }
  };

  return (
    <div className="card card-pad" style={{ marginBottom: 14 }}>
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: 14,
          flexWrap: "wrap",
        }}
      >
        <div>
          <div style={{ fontSize: 13.5, fontWeight: 600 }}>
            This account uses an Apple Ads credit line
          </div>
          <div
            style={{ fontSize: 12.5, color: "var(--c-text-3)", marginTop: 2 }}
          >
            Turn on if this Apple Ads account pays by monthly invoicing
            (line of credit). Campaigns created in Campaign Studio will be
            attached to the budget order or invoice you choose below — without
            it Apple parks them as “Missing budget order”.
          </div>
        </div>
        <Switch
          on={enabled}
          onChange={(next) => {
            setEnabled(next);
            dirtyMark();
          }}
        />
      </div>

      {enabled && (
        <div style={{ marginTop: 16, display: "flex", flexDirection: "column", gap: 14 }}>
          {/* Mode selector */}
          <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
            {(
              [
                { v: "budget_order", label: "Budget Order" },
                { v: "loc_invoice", label: "Standard invoice" },
              ] as const
            ).map((opt) => {
              const active = mode === opt.v;
              return (
                <button
                  key={opt.v}
                  type="button"
                  onClick={() => {
                    setMode(opt.v);
                    dirtyMark();
                  }}
                  style={{
                    padding: "8px 16px",
                    borderRadius: 10,
                    border: active
                      ? "1.5px solid var(--c-accent-600)"
                      : "1px solid var(--c-border, #d6dae0)",
                    background: active ? "var(--c-accent-600)" : "var(--c-surface)",
                    color: active ? "#fff" : "var(--c-text)",
                    fontWeight: 600,
                    fontSize: 13,
                    cursor: "pointer",
                  }}
                >
                  {opt.label}
                </button>
              );
            })}
          </div>

          {mode === "budget_order" ? (
            <div
              style={{
                display: "grid",
                gridTemplateColumns: "200px 1fr",
                gap: 12,
                alignItems: "center",
              }}
            >
              <div
                className="label"
                style={{ fontSize: 13, color: "var(--c-text-2)" }}
              >
                Budget Order <span style={{ color: "var(--c-neg-600)" }}>*</span>
              </div>
              <div>
                <select
                  className="input"
                  value={boId ?? ""}
                  onChange={(e) => {
                    const id = e.target.value ? Number(e.target.value) : null;
                    setBoId(id);
                    const opt = (boQuery.data ?? []).find((b) => b.id === id);
                    setBoName(opt?.name ?? null);
                    dirtyMark();
                  }}
                >
                  <option value="">
                    {boQuery.isLoading ? "Loading…" : "Select a budget order…"}
                  </option>
                  {(boQuery.data ?? []).map((b) => (
                    <option key={b.id} value={b.id}>
                      {b.name}
                      {b.status ? ` · ${b.status}` : ""}
                      {b.budget_amount
                        ? ` · ${b.currency ?? ""} ${b.budget_amount}`
                        : ""}
                    </option>
                  ))}
                </select>
                {boQuery.isError && (
                  <div
                    className="muted"
                    style={{ fontSize: 12, marginTop: 6, color: "var(--c-neg-600)" }}
                  >
                    Couldn’t load budget orders from Apple. Check the ASA
                    connection above, then reopen this page.
                  </div>
                )}
                {!boQuery.isLoading &&
                  !boQuery.isError &&
                  (boQuery.data ?? []).length === 0 && (
                    <div className="muted" style={{ fontSize: 12, marginTop: 6 }}>
                      No active budget orders found for this account.
                    </div>
                  )}
              </div>
            </div>
          ) : (
            <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
              <Field
                label="Billing contact email"
                required
                value={billingEmail}
                placeholder="billing@company.com"
                onChange={(v) => {
                  setBillingEmail(v);
                  dirtyMark();
                }}
              />
              <Field
                label="Buyer email"
                required
                value={buyerEmail}
                placeholder="buyer@company.com"
                onChange={(v) => {
                  setBuyerEmail(v);
                  dirtyMark();
                }}
              />
              <Field
                label="Buyer name"
                required
                value={buyerName}
                placeholder="Jane Buyer"
                onChange={(v) => {
                  setBuyerName(v);
                  dirtyMark();
                }}
              />
              <Field
                label="Client name"
                value={clientName}
                placeholder="Advertiser / product (agency accounts)"
                onChange={(v) => {
                  setClientName(v);
                  dirtyMark();
                }}
              />
              <Field
                label="Order number (PO)"
                value={orderNumber}
                placeholder="PO number (agency accounts)"
                onChange={(v) => {
                  setOrderNumber(v);
                  dirtyMark();
                }}
              />
              <div className="muted" style={{ fontSize: 12 }}>
                Client name + Order number are required only for agency-type
                Apple Ads accounts; leave blank otherwise.
              </div>
            </div>
          )}
        </div>
      )}

      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          marginTop: 16,
          gap: 12,
          flexWrap: "wrap",
        }}
      >
        <div style={{ fontSize: 12 }}>
          {err && <span style={{ color: "var(--c-neg-600)" }}>✗ {err}</span>}
          {!err && saved && (
            <span style={{ color: "var(--c-pos-600)" }}>✓ Saved</span>
          )}
        </div>
        <Btn variant="primary" onClick={() => void onSave()} disabled={save.isPending}>
          {save.isPending ? "Saving…" : "Save"}
        </Btn>
      </div>
    </div>
  );
}
